import React, { useEffect, useContext, useState, useMemo } from "react";
import {
  Heading,
  RoundButton,
  Text,
  Button,
  Loading,
  Pill,
  CardNotification,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Classroom, ClassroomBookingValidationErrorUnion } from "@models/ISchema";
import { BookingContext } from "../../context/BookingContext";
import { Types } from "../../context/request.reducer";
import { Types as TypesSearch } from "../../context/search.reducer";
import { Header } from "@common/components";
import useGetEventTypes from "../../hooks/useGetEventTypes";
import useGetIntervals from "../../hooks/useIntervals";
import { createClassroomBookingMutation } from "../../graphql/createClassroomBooking.mutation";
import { ContextEDH } from "@context/ContextEDH";
import { getDateNumber } from "@utils/formats";
import Form from "../../components/RequestForm/RequestForm";
import Days from "../../components/RequestDays/RequestDays";
import Errors from "../../components/RequestErrors/RequestErrors";
import Layout from "../../../../common/layout/Layout";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import css from "./request.module.scss";

const Request = () => {
  const history = useHistory();
  const { state, dispatch } = useContext(BookingContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const [createClassroomBookingAction] = useMutation(createClassroomBookingMutation);
  const { origin, scenario, contextUrl: urlContext, isAdminBooking } = useBookingContextUrl();
  const [loading, setLoading] = useState(false);
  const [eventTypes, getEventTypes] = useGetEventTypes();
  const [intervals, getIntervals] = useGetIntervals();
  const { t } = useTranslation();
  const { adaptedHomeUrl } = useNavigationUrl();

  const pathQueryParams = !isAdminBooking ? `?scenario=${scenario}&origin=${origin}` : "";

  useEffect(() => {
    if (!state?.search?.selectedClassrooms?.length) {
      history.push(`/booking/search/${urlContext}${pathQueryParams}`);
    }

    if (!state?.request?.eventTypesOptions && !eventTypes?.data && !eventTypes?.loading) {
      getEventTypes();
    }

    if (!state?.search?.intervals?.length && !intervals?.data) {
      getIntervals(scenario);
    }
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    const title = state?.request?.title?.value;
    const eventType = state?.request?.eventType?.value;
    const responsible = state?.request?.userResponsible?.value;
    let isSubmit = true;

    if (!title || title === "") {
      dispatch({
        type: Types.SetTitle,
        payload: { value: title, error: true },
      });
      isSubmit = false;
      setLoading(false);
    }
    if (!eventType || eventType === "") {
      dispatch({
        type: Types.SetEventType,
        payload: { value: state?.request?.eventType?.value, error: true },
      });
      isSubmit = false;
      setLoading(false);
    }
    if (isAdminBooking && (!responsible || responsible === "")) {
      dispatch({
        type: Types.SetUserResponsible,
        payload: { value: state?.request?.userResponsible?.value, error: true },
      });
      isSubmit = false;
      setLoading(false);
    }

    if (isSubmit) {
      const capacity = state?.search?.requirements?.people?.count;
      const description = state?.request?.description?.value;
      const isRecurrent = state?.search?.recurrence;
      const programId = state?.search?.preferences?.program?.value;
      const user = isAdminBooking
        ? state?.request?.userResponsible?.value
        : stateEDH?.base?.base?.user?.id;

      const sessionAssignmentBookings = state?.search?.selectedClassrooms?.map(
        ({ classroom, dates }) => ({
          classroomId: classroom?.id,
          assignedDates: dates?.map(({ day, start, end }) => {
            const [startHour, startMinute, startSecond] = start?.value?.value?.split(":");
            const [endHour, endMinute, endSecond] = end?.value?.value?.split(":");

            return {
              start: `${day?.dateValue} ${getDateNumber(startHour)}:${getDateNumber(
                startMinute || "00",
              )}:${getDateNumber(startSecond || "00")}`,
              end: `${day?.dateValue} ${getDateNumber(endHour)}:${getDateNumber(
                endMinute || "00",
              )}:${getDateNumber(endSecond || "00")}`,
            };
          }),
        }),
      );

      const variables = {
        scenarioId: scenario,
        originId: origin,
        input: {
          dryRun: false,
          skipValidations: false,
          changesets: {
            creates: [
              {
                sessionAssignmentBookings,
                responsibleId: user,
                capacity,
                isRecurrent,
                title,
                programId,
                description,
                classroomBookingTypeId: eventType,
              },
            ],
          },
        },
      };

      const response = await createClassroomBookingAction({
        variables,
      });

      const errors: ClassroomBookingValidationErrorUnion[] =
        response?.data?.cube?.createClassroomBooking?.validationErrors;

      if (errors && errors.length > 0) {
        dispatch({ type: Types.SetErrors, payload: errors });
        setLoading(false);
      } else {
        //sucess
        setLoading(false);
        dispatch({ type: Types.ClearRequest, payload: null });
        dispatch({ type: TypesSearch.ClearSearch, payload: null });
        history.replace(
          `/booking/list/${urlContext}${!isAdminBooking ? `?campus=${scenario}` : ""}`,
        );
      }
    }
  };

  const bookingResumeDetails = useMemo(() => {
    const selectedClassrooms = state?.search?.selectedClassrooms ?? [];
    const uniqueCampuses = new Set(
      selectedClassrooms?.map(({ classroom }) => classroom?.building?.campus?.name),
    );
    const uniqueBuildings = new Set(
      selectedClassrooms?.map(({ classroom }) => classroom?.building?.name),
    );
    const uniqueClassroomTypes = new Set(
      selectedClassrooms?.map(({ classroom }) => classroom?.classroomType?.name),
    );
    const uniqueClassrooms = Object.values(
      selectedClassrooms.reduce((acc, { classroom }) => {
        if (classroom?.code && !acc[classroom.code]) acc[classroom.code] = classroom;
        return acc;
      }, {} as Record<string, Classroom>),
    );

    return {
      campus: Array.from(uniqueCampuses)?.join(", ") ?? "-",
      building: Array.from(uniqueBuildings)?.join(", ") ?? "-",
      classroomType: Array.from(uniqueClassroomTypes)?.join(", ") ?? "-",
      classroom: uniqueClassrooms,
    };
  }, [state?.search?.selectedClassrooms]);

  const someSelectedClassroomIsSpecial = useMemo(() => {
    const selectedClassrooms = state?.search?.selectedClassrooms ?? [];
    return selectedClassrooms.some(({ classroom }) => classroom?.isSpecialTagValue);
  }, [state?.search?.selectedClassrooms]);

  return (
    <Layout contextSearch={false}>
      <section className={css.bookingRequest}>
        {loading && <Loading />}

        <Header
          breadcrumbItems={[
            { title: t("home.breadcrumb.current"), path: adaptedHomeUrl() },
            { title: t("booking.title") },
          ]}
          title={t("booking.title")}
        />

        <section className={css.bookingRequest_content}>
          <div className={css.contentHeader}>
            <RoundButton
              size="sm"
              variant="ghost"
              icon="chevron-left"
              onClick={() => {
                const searchPath = `/booking/search/${urlContext}${pathQueryParams}`;

                dispatch({ type: TypesSearch.SetView, payload: "result" });
                history.replace(searchPath);
              }}
            />

            <Heading type="h2">
              {t("booking.booking-request.title", {
                name: state?.search?.classroom?.name ?? "",
              })}
            </Heading>
          </div>

          <div className={css.contentResume}>
            <div className={css.contentResume_classroom}>
              <div className={css.classroomDetails}>
                <Text className={css.classroomDetails_label} type="sm">
                  {t("booking.booking-request.resume.campus")}
                </Text>

                <Text className={css.classroomDetails_value}>
                  {bookingResumeDetails?.campus ?? "-"}
                </Text>
              </div>

              <div className={css.classroomDetails}>
                <Text className={css.classroomDetails_label} type="sm">
                  {t("booking.booking-request.resume.building")}
                </Text>

                <Text className={css.classroomDetails_value}>
                  {bookingResumeDetails?.building ?? "-"}
                </Text>
              </div>

              <div className={css.classroomDetails}>
                <Text className={css.classroomDetails_label} type="sm">
                  {t("booking.booking-request.resume.classroom-type")}
                </Text>

                <Text className={css.classroomDetails_value}>
                  {bookingResumeDetails?.classroomType ?? "-"}
                </Text>
              </div>

              <div className={css.classroomDetails}>
                <Text className={css.classroomDetails_label} type="sm">
                  {t("booking.booking-request.resume.selected-classroom")}
                </Text>

                <Text className={css.classroomDetails_value}>
                  {bookingResumeDetails?.classroom?.length
                    ? bookingResumeDetails?.classroom.map((classroom, index) => (
                        <span key={classroom?.code} className={css.classroomDetails_classroom}>
                          {classroom?.name}
                          {!!classroom?.isSpecialTagValue && (
                            <Pill
                              className={css.specialTag}
                              icon="alert-triangle"
                              iconColor="warning-60"
                              textColor="warning-60"
                              color="warning-opacity"
                            >
                              {t("booking.classroom-tags.special")}
                            </Pill>
                          )}
                          {index !== bookingResumeDetails?.classroom?.length - 1 && ", "}
                        </span>
                      ))
                    : "-"}
                </Text>
              </div>
            </div>

            <div className={css.contentResume_separator} />

            <Days />
          </div>

          <Form />
          <Errors />

          {someSelectedClassroomIsSpecial && (
            <CardNotification
              title={t("booking.booking-request.notifications.special-classroom.title")}
              state="warning"
              outlined
            >
              <Text type="sm">
                {t("booking.booking-request.notifications.special-classroom.description")}
              </Text>
            </CardNotification>
          )}

          <footer className={css.contentFooter}>
            <Button
              variant="ghost"
              onClick={() => {
                dispatch({ type: Types.ClearRequest, payload: null });
                dispatch({ type: TypesSearch.ClearSearch, payload: null });
                history.push(`/booking/list/${urlContext}`);
              }}
            >
              {t("booking.booking-request.actions.cancel")}
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {t("booking.booking-request.actions.submit")}
            </Button>
          </footer>
        </section>
      </section>
    </Layout>
  );
};

export default Request;
